// src/pages/LeadershipForecast.jsx
import React, { useEffect, useMemo, useState } from "react";
import { fmt, PRICES } from "../config/devices";
import { FilterBtn } from "../components/UI";

const LEADERSHIP_CAMPUSES = ["CCK", "CCW"];
const ADD_TO_BUDGET = "add-to-budget";

function emptyReviewState() {
  return { devices: {}, miscItems: [], finalizedAt: null, finalizedBy: null, finalizedCampuses: {}, jiraTickets: [] };
}

function campusFinalization(state, campus, devices) {
  if (!state) return null;
  const direct = state.finalizedCampuses?.[campus];
  if (direct?.finalizedAt) return direct;

  const legacyTicket = (state.jiraTickets || []).find(ticket => ticket.campus === campus);
  if (state.finalizedAt && legacyTicket) {
    return {
      finalizedAt: state.finalizedAt,
      finalizedBy: state.finalizedBy,
      jiraKey: legacyTicket.key,
      jiraTickets: [legacyTicket],
    };
  }

  const campusDevices = devices.filter(d => d.campus === campus);
  const hasReviewedDevices = campusDevices.length > 0 && campusDevices.every(d => {
    const status = state.devices?.[d.serial]?.status || "unreviewed";
    return status !== "unreviewed";
  });

  return state.finalizedAt && hasReviewedDevices
    ? { finalizedAt: state.finalizedAt, finalizedBy: state.finalizedBy, jiraKey: state.jiraKey, jiraTickets: state.jiraTickets || [] }
    : null;
}

export default function LeadershipForecast({ computeDevices, computeDevices5Year, priceMap, devicePrice, lwtLoadedAt, lwtLoading, lwtFallback }) {
  const lifecycleYears = 5;
  const prices = priceMap || PRICES;
  const [campusFilter, setCampusFilter] = useState("All");
  // The former 4-year scenario remains internal historical comparison data only.
  const [reviewStates, setReviewStates] = useState({});
  const [reviewLoading, setReviewLoading] = useState(false);
  const currentYear = new Date().getFullYear();
  const costBasis = devicePrice ?? 1704;
  const scenarioDevices = computeDevices5Year || [];
  const reviewProgram = "5-year";

  // Public leadership links always show the ratified lifecycle.

  const filtered = useMemo(
    () => campusFilter === "All" ? scenarioDevices : scenarioDevices.filter(d => d.campus === campusFilter),
    [scenarioDevices, campusFilter]
  );

  const years = useMemo(() => {
    const min = currentYear + 1;
    const max = lifecycleYears === 5 ? currentYear + 5 : currentYear + 4;
    const all = [];
    for (let y = min; y <= max; y++) all.push(y);
    return all;
  }, [scenarioDevices, currentYear, lifecycleYears]);
  useEffect(() => {
    let cancelled = false;
    const yearsToLoad = years.slice(0, 6);
    if (!yearsToLoad.length) return undefined;

    setReviewStates({});
    setReviewLoading(true);
    Promise.all(yearsToLoad.map(async year => {
      try {
        const res = await fetch(`/api/budget-review?year=${encodeURIComponent(year)}&program=${encodeURIComponent(reviewProgram)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return [year, { ...emptyReviewState(), ...(data.state || {}) }];
      } catch {
        return [year, emptyReviewState()];
      }
    }))
      .then(entries => {
        if (!cancelled) setReviewStates(Object.fromEntries(entries));
      })
      .finally(() => {
        if (!cancelled) setReviewLoading(false);
      });

    return () => { cancelled = true; };
  }, [years, reviewProgram]);

  const yearData = useMemo(() =>
    years.map(year => {
      const devices = filtered.filter(d => Number(d.eolYear) === Number(year));
      const state = reviewStates[year] || emptyReviewState();
      const selectedCampuses = campusFilter === "All" ? LEADERSHIP_CAMPUSES : [campusFilter];
      const reviewedCampusData = selectedCampuses.map(campus => {
        const campusDevices = devices.filter(d => d.campus === campus);
        const finalization = campusFinalization(state, campus, campusDevices);
        if (!finalization) {
          return { campus, finalized: false, projected: campusDevices.length, reviewed: null, budget: null };
        }
        const manuallyAddedDevices = filtered.filter(d =>
          d.campus === campus &&
          (state.devices?.[d.serial]?.status || "unreviewed") === ADD_TO_BUDGET &&
          !campusDevices.some(device => device.serial === d.serial)
        );
        const miscItems = (state.miscItems || []).filter(item =>
          !item.removed &&
          Number(item.year) === Number(year) &&
          item.campus === campus
        );
        const reviewedDevices = [
          ...campusDevices.filter(d => (state.devices?.[d.serial]?.status || "unreviewed") !== "excluded"),
          ...manuallyAddedDevices,
        ];
        return {
          campus,
          finalized: true,
          projected: campusDevices.length,
          reviewed: reviewedDevices.length + miscItems.length,
          budget: reviewedDevices.reduce((sum, d) => sum + (prices[d.deviceType] || 0), 0) + miscItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
          finalizedAt: finalization.finalizedAt,
        };
      });
      const reviewedCampuses = reviewedCampusData.filter(c => c.finalized);
      const reviewedBudget = reviewedCampuses.reduce((sum, c) => sum + (c.budget || 0), 0);
      const reviewedTotal = reviewedCampuses.reduce((sum, c) => sum + (c.reviewed || 0), 0);
      const status = reviewedCampuses.length === 0
        ? "Forecast"
        : reviewedCampuses.length === selectedCampuses.length
          ? "ICT reviewed"
          : "Partially reviewed";
      return {
        year,
        total: devices.length,
        cck: devices.filter(d => d.campus === "CCK").length,
        ccw: devices.filter(d => d.campus === "CCW").length,
        budget: devices.reduce((sum, d) => sum + (prices[d.deviceType] || 0), 0),
        reviewedBudget,
        reviewedTotal,
        reviewedCampusData,
        status,
      };
    }), [filtered, years, prices, reviewStates, campusFilter]);

  const totals = useMemo(() => ({
    total: filtered.length,
    cck: filtered.filter(d => d.campus === "CCK").length,
    ccw: filtered.filter(d => d.campus === "CCW").length,
    budget: filtered.reduce((sum, d) => sum + (prices[d.deviceType] || 0), 0),
    reviewedBudget: yearData.reduce((sum, y) => sum + y.reviewedBudget, 0),
    reviewedTotal: yearData.reduce((sum, y) => sum + y.reviewedTotal, 0),
  }), [filtered, prices, yearData]);

  const maxBudget = Math.max(...yearData.map(y => y.budget), 1);
  const maxReviewedBudget = Math.max(...yearData.map(y => y.reviewedBudget || 0), 1);
  const chartMax = Math.max(maxBudget, maxReviewedBudget, 1);
  const nextRefreshData = yearData.find(y => y.total > 0);
  const proposalStartYear = currentYear + 1;
  const comparisonEndYear = currentYear + 4;
  const proposalEndYear = currentYear + 5;
  const proposedComparisonBudget = yearData
    .filter(y => y.year >= proposalStartYear && y.year <= comparisonEndYear)
    .reduce((sum, y) => sum + y.budget, 0);
  const forwardProposalData = yearData.filter(y => y.year >= proposalStartYear && y.year <= proposalEndYear);
  const forwardProposalBudget = forwardProposalData.reduce((sum, y) => sum + y.budget, 0);
  const forwardProposalTotal = forwardProposalData.reduce((sum, y) => sum + y.total, 0);
  const deferredYearData = yearData.find(y => y.year === proposalEndYear) || { total: 0, budget: 0 };
  const currentProgrammeDevices = computeDevices.filter(d =>
    (campusFilter === "All" || d.campus === campusFilter) &&
    Number(d.eolYear) >= proposalStartYear &&
    Number(d.eolYear) <= comparisonEndYear
  );
  const currentProgrammeOutlook = currentProgrammeDevices.reduce((sum, d) => sum + (prices[d.deviceType] || 0), 0);
  const timingDifference = currentProgrammeOutlook - proposedComparisonBudget;
  const nextRefreshDeviceSummary = nextRefreshData
    ? [
        {
          label: "Budgeted",
          color: nextRefreshData.year === currentYear ? "var(--warn)" : "var(--accent)",
          text: `${nextRefreshData.total} devices`,
        },
        ...(nextRefreshData.reviewedTotal ? [{
          label: "Finalised",
          color: "var(--success)",
          text: `${nextRefreshData.reviewedTotal} devices`,
        }] : []),
      ]
    : "no upcoming devices shown";
  const generatedAt = lwtLoadedAt
    ? new Date(lwtLoadedAt).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })
    : new Date().toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });

  const commonSummaryCards = [
    ["Forecast Scope", `${totals.total}`, campusFilter === "All" ? "staff devices across both campuses" : `staff devices at ${campusFilter}`],
  ];
  const summaryCards = lifecycleYears === 5
    ? [
        ...commonSummaryCards,
        ["Forward Replacement Requirement", fmt(forwardProposalBudget), `${proposalStartYear}-${proposalEndYear} | ${forwardProposalTotal} devices`],
        [`${proposalStartYear}-${comparisonEndYear} Impact vs Former 4-Year`, timingDifference >= 0 ? fmt(timingDifference) + " lower" : fmt(Math.abs(timingDifference)) + " higher", `${deferredYearData.total} replacements | ${fmt(deferredYearData.budget)} deferred into ${proposalEndYear}`],
        ["Current Cost Basis", fmt(costBasis), "per staff device"],
        ["Next Refresh Year", nextRefreshData?.year || "—", nextRefreshDeviceSummary],
      ]
    : [
        ...commonSummaryCards,
        ["Forward Replacement Requirement", fmt(currentProgrammeOutlook), `${proposalStartYear}-${comparisonEndYear} | ${currentProgrammeDevices.length} devices`],
        ["Current Cost Basis", fmt(costBasis), "per staff device"],
        ["Next Refresh Year", nextRefreshData?.year || "—", nextRefreshDeviceSummary],
      ];
  return (
    <div className="fade-up" style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--text)" }}>
      <main style={{ maxWidth: 1180, margin: "0 auto", padding: "34px 28px 48px" }}>
        <header style={{ textAlign: "center", margin: "0 auto 30px", maxWidth: 820 }}>
          <img src="/cwlogo.png" alt="Christway College" style={{ width: 96, display: "block", margin: "0 auto 8px" }} />
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>Device Lifecycle Tool</div>
          <div style={{ fontSize: 11, color: "#2CFF05", marginTop: 3, marginBottom: 18 }}>Built by John Vilar</div>
          <div style={{ fontSize: 10, color: "var(--accent)", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 7 }}>Leadership Summary</div>
          <div style={{ display: "inline-block", color: "var(--success)", border: "1px solid rgba(34,197,94,0.35)", background: "rgba(34,197,94,0.08)", borderRadius: 6, padding: "5px 9px", fontSize: 11, fontWeight: 800, marginBottom: 14 }}>Ratified 5-Year Lifecycle</div>
          <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 28, lineHeight: 1.12, fontWeight: 850, letterSpacing: "-0.02em", marginBottom: 12 }}>
              5-Year Device Refresh Forecast
          </h1>
          <p style={{ color: "var(--muted)", fontSize: 14, lineHeight: 1.65, maxWidth: 760, margin: "0 auto" }}>
            This page presents projected staff device refresh requirements under the ratified five-year lifecycle. It is part of a wider ICT tool used by the team to review Jira asset records, verify device activity, and confirm the exact refresh list before final quotes are requested.
          </p>
          <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)" }}>
            The completed 2026 refresh is excluded from forward planning; remaining devices are forecast on the five-year lifecycle.
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: "var(--muted)" }}>
            Generated from live lifecycle data · {generatedAt}{lwtFallback ? " · using cached LWT fallback data" : ""}
          </div>
        </header>

        {lwtLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(79,156,249,0.08)", border: "1px solid rgba(79,156,249,0.25)", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "var(--accent)", marginBottom: 18 }}>
            <span className="spinner" style={{ width: 14, height: 14, borderTopColor: "var(--accent)", flexShrink: 0 }} />
            Loading forecast data…
          </div>
        )}
        {reviewLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.22)", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "var(--success)", marginBottom: 18 }}>
            <span className="spinner" style={{ width: 14, height: 14, borderTopColor: "var(--success)", flexShrink: 0 }} />
            Checking finalised Budget Review data…
          </div>
        )}

        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12, marginBottom: 22 }}>
          {summaryCards.map(([label, value, sub]) => (
            <div key={label} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 10, padding: "16px 18px" }}>
              <div style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>{label}</div>
              <div style={{ fontSize: 24, fontWeight: 850, color: label.includes("Projected") || label.includes("Reviewed") || label.includes("Cost") ? "var(--warn)" : "var(--text)", lineHeight: 1.15 }}>{value}</div>
              {Array.isArray(sub) ? (
                <div style={{ display: "grid", gap: 3, marginTop: 7 }}>
                  {sub.map(item => (
                    <div key={item.label} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 11, lineHeight: 1.25, color: "var(--muted)", whiteSpace: "nowrap" }}>
                      <span style={{ color: item.color, fontWeight: 800 }}>{item.label}</span>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.text}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 5 }}>{sub}</div>
              )}
            </div>
          ))}
        </section>

        <section style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
          <span style={{ color: "var(--muted)", fontSize: 12, fontWeight: 700, marginRight: 4 }}>Campus</span>
          {["All", "CCK", "CCW"].map(c => (
            <FilterBtn key={c} label={c} active={campusFilter === c} onClick={() => setCampusFilter(c)} color={c === "CCK" ? "var(--cck)" : c === "CCW" ? "var(--ccw)" : undefined} />
          ))}
        </section>

        <section style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: 24, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "baseline", marginBottom: 46, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Annual Forecast</div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Budgeted vs finalised refresh budget</div>
            </div>
            <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", fontSize: 12, color: "var(--muted)" }}>
              <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: "var(--accent)", marginRight: 6 }} />Projected (budgeted)</span>
              <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: "var(--success)", marginRight: 6 }} />Finalised (actual)</span>
              <span>Cost basis: <span style={{ color: "var(--warn)", fontWeight: 800 }}>{fmt(costBasis)}</span> per staff device</span>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 230, paddingTop: 8 }}>
            {yearData.map(y => {
              const height = chartMax > 0 ? Math.max(Math.round((y.budget / chartMax) * 150), y.budget > 0 ? 6 : 0) : 0;
              const reviewedHeight = chartMax > 0 ? Math.max(Math.round((y.reviewedBudget / chartMax) * 150), y.reviewedBudget > 0 ? 6 : 0) : 0;
              const isCurrent = y.year === currentYear;
              return (
                <div key={y.year} style={{ flex: 1, minWidth: 54, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                  <div style={{ height: 44, maxWidth: "100%", display: "grid", alignContent: "end", justifyItems: "center", gap: 2, fontSize: 10, lineHeight: 1.15, fontFamily: "var(--font-mono)", textAlign: "center", overflow: "hidden", overflowWrap: "anywhere" }}>
                    {y.budget ? (
                      <>
                        <span style={{ color: isCurrent ? "var(--warn)" : "var(--accent)" }}>Budgeted {fmt(y.budget)}</span>
                        {y.reviewedBudget ? (
                          <span style={{ color: "var(--success)" }}>Finalised {fmt(y.reviewedBudget)}</span>
                        ) : null}
                      </>
                    ) : "—"}
                  </div>
                  <div style={{ width: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 4 }}>
                    <div title="Projected (budgeted)" style={{ flex: "1 1 0", maxWidth: 34, height, background: isCurrent ? "var(--warn)" : "linear-gradient(180deg, rgba(79,156,249,0.85), rgba(79,156,249,0.32))", borderRadius: "5px 5px 0 0", minHeight: y.budget > 0 ? 6 : 0 }} />
                    <div title="Finalised (actual)" style={{ flex: "1 1 0", maxWidth: 34, height: reviewedHeight, background: y.reviewedBudget ? "linear-gradient(180deg, rgba(34,197,94,0.95), rgba(34,197,94,0.38))" : "rgba(255,255,255,0.06)", borderRadius: "5px 5px 0 0", minHeight: y.reviewedBudget > 0 ? 6 : 0 }} />
                  </div>
                  <div style={{ fontSize: 12, fontFamily: "var(--font-mono)", color: isCurrent ? "var(--warn)" : "var(--text)", fontWeight: isCurrent ? 800 : 600 }}>{y.year}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)", textAlign: "center", display: "grid", gap: 2, lineHeight: 1.2 }}>
                    <span style={{ color: isCurrent ? "var(--warn)" : "var(--accent)" }}>{y.total || 0} budgeted</span>
                    {y.reviewedTotal ? <span style={{ color: "var(--success)" }}>{y.reviewedTotal} finalised</span> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--bg3)", borderBottom: "1px solid var(--border)" }}>
                {["Year", "Projected (Budgeted)", "Finalised (Actual)", "Difference", "Campus Split", "Status"].map((h, i) => (
                  <th key={h} style={{ padding: "13px 16px", fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", textAlign: i > 0 ? "right" : "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {yearData.map((y, i) => {
                const diff = y.status === "ICT reviewed" ? y.reviewedBudget - y.budget : null;
                const statusColor = y.status === "ICT reviewed" ? "var(--success)" : y.status === "Partially reviewed" ? "var(--warn)" : "var(--muted)";
                return (
                  <tr key={y.year} style={{ borderBottom: "1px solid var(--border)", background: y.year === currentYear ? "rgba(245,158,11,0.05)" : i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.012)" }}>
                    <td style={{ padding: "13px 16px", fontWeight: 800, color: y.year === currentYear ? "var(--warn)" : "var(--text)", fontFamily: "var(--font-mono)" }}>{y.year}</td>
                    <td style={{ padding: "13px 16px", textAlign: "right", fontWeight: 700, fontFamily: "var(--font-mono)" }}>
                      {y.total || "—"}
                      <div style={{ color: y.budget ? "var(--warn)" : "var(--muted)", fontSize: 11, marginTop: 3 }}>{y.budget ? fmt(y.budget) : "—"}</div>
                    </td>
                    <td style={{ padding: "13px 16px", textAlign: "right", fontWeight: 750, fontFamily: "var(--font-mono)", color: y.reviewedBudget ? "var(--success)" : "var(--muted)" }}>
                      {y.reviewedBudget ? y.reviewedTotal : "—"}
                      <div style={{ fontSize: 11, marginTop: 3 }}>{y.reviewedBudget ? fmt(y.reviewedBudget) : "awaiting finalise"}</div>
                    </td>
                    <td style={{ padding: "13px 16px", textAlign: "right", fontWeight: 750, fontFamily: "var(--font-mono)", color: diff == null ? "var(--muted)" : diff > 0 ? "var(--warn)" : diff < 0 ? "var(--success)" : "var(--muted)" }}>
                      {diff == null ? "—" : diff === 0 ? "No change" : fmt(Math.abs(diff))}
                      {diff != null && diff !== 0 && <div style={{ fontSize: 11, marginTop: 3 }}>{diff > 0 ? "above projection" : "below projection"}</div>}
                    </td>
                    <td style={{ padding: "13px 16px", textAlign: "right", fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>
                      {y.reviewedCampusData.map(c => (
                        <div key={c.campus} style={{ color: c.campus === "CCK" ? "var(--cck)" : "var(--ccw)" }}>
                          {c.campus} {c.finalized ? `${c.reviewed}/${c.projected}` : `—/${c.projected}`}
                        </div>
                      ))}
                    </td>
                    <td style={{ padding: "13px 16px", textAlign: "right", color: statusColor, fontWeight: 800, whiteSpace: "nowrap" }}>{y.status}</td>
                  </tr>
                );
              })}
              <tr style={{ background: "rgba(255,255,255,0.04)", borderTop: "2px solid var(--border2)" }}>
                <td style={{ padding: "13px 16px", fontWeight: 850 }}>Total</td>
                <td style={{ padding: "13px 16px", textAlign: "right", fontWeight: 850, fontFamily: "var(--font-mono)" }}>{totals.total}<div style={{ color: "var(--warn)", fontSize: 11, marginTop: 3 }}>{fmt(totals.budget)}</div></td>
                <td style={{ padding: "13px 16px", textAlign: "right", fontWeight: 850, color: totals.reviewedBudget ? "var(--success)" : "var(--muted)", fontFamily: "var(--font-mono)" }}>{totals.reviewedTotal || "—"}<div style={{ fontSize: 11, marginTop: 3 }}>{totals.reviewedBudget ? fmt(totals.reviewedBudget) : "awaiting finalise"}</div></td>
                <td style={{ padding: "13px 16px", textAlign: "right", fontWeight: 850, color: "var(--muted)", fontFamily: "var(--font-mono)" }}>—</td>
                <td style={{ padding: "13px 16px", textAlign: "right", fontWeight: 850, color: "var(--muted)" }}>{campusFilter === "All" ? `${totals.cck} CCK · ${totals.ccw} CCW` : campusFilter}</td>
                <td style={{ padding: "13px 16px", textAlign: "right", fontWeight: 900, color: "var(--accent)" }}>Summary</td>
              </tr>
            </tbody>
          </table>
        </section>
      </main>
    </div>
  );
}
